// src/scenes/MainMenuScene.js
// v3 - Uses BaseMenuScene, UIController, SceneManager

import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import { showAuthOverlay } from "../ui/authUI.js";
import { auth } from "../firebaseConfig.js";
import UIController from "../ui/uiController.js";
import sceneManager from "../utils/SceneManager.js";
import { hasSpecialAccess } from "../services/specialAccessService.js";

class MainMenuScene extends BaseMenuScene {
    constructor() {
        super({ key: "MainMenuScene" });
        this.centered = true;
    }

    create() {
        super.create();
        this.clearMenuItems();

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;
        const centerY = height / 2;

        const topRibbon = document.getElementById("top-ribbon");
        if (topRibbon) topRibbon.style.display = 'none';
        const gameContainer = document.getElementById("game-container");
        if (gameContainer) { gameContainer.style.top = "0px"; gameContainer.style.height = "100%"; }

        this.uiElements.title = this.add.text(centerX, centerY - 80, "DeckOps.ai", {
            fontFamily: 'monospace', fontSize: '48px', color: '#00FF77', // Consistent with BootScene styling
            stroke: '#003322', strokeThickness: 4,
            shadow: { offsetX: 2, offsetY: 2, color: '#003300', blur: 5, fill: true }
        }).setOrigin(0.5).setAlpha(0);
        const buttonY = centerY + 50;
        const buttonSpacing = 200;

        this.uiElements.playButton = this.createStyledButton(
            centerX - buttonSpacing / 2, buttonY, "Play",
            () => {
                if (!this.input.enabled) {
                    console.warn(`[${this.scene.key}] Play button clicked, but input is disabled!`);
                    return;
                }
                console.log(`[${this.scene.key}] Play button clicked - Requesting transition via SceneManager`);
                if (!sceneManager) {
                    console.error("SceneManager is not imported or available!");
                    this.showMessage("Critical Error: Navigation system failed.", 3000, true);
                    return;
                }

        // --- MODIFIED TARGET SCENE LOGIC ---
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess(); // Check for special access
        let targetScene;

        // Check feature flags for DOM scenes
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        
        if (user && user.emailVerified) {
            targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
        } else if (specialAccess) { // If guest+ access, go to AuthenticatedMenu
            targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
        } else { // Otherwise, go to GuestMenu
            targetScene = useDOMMenus ? "GuestMenuSceneDOM" : "GuestMenuScene";
        }
        // --- END MODIFIED ---
                console.log(`[${this.scene.key}] Target scene determined: ${targetScene}`);
                sceneManager.switchToScene(targetScene);
                console.log(`[${this.scene.key}] Called sceneManager.switchToScene('${targetScene}')`);
            },
            MenuUIUtils.COLORS.primary, 180, 60, 'sizeLg'
        );
        if(this.uiElements.playButton) this.uiElements.playButton.setAlpha(0);

        this.uiElements.signInButton = this.createStyledButton(
            centerX + buttonSpacing / 2, buttonY, "Sign In",
            () => {
                console.log("Sign In button clicked in MainMenuScene");
                if (window.UIController) { window.UIController.setActiveScene(this); }
                const authOverlay = document.getElementById('auth-overlay');
                if (authOverlay) {
                    const authTitle = document.getElementById('auth-title'); if (authTitle) authTitle.textContent = "Sign In";
                    const signupSection = document.getElementById('signup-section'); if (signupSection) signupSection.classList.add('hidden-section');
                    const signinSubmitBtn = document.getElementById('signin-submit-btn-auth'); if (signinSubmitBtn) signinSubmitBtn.classList.remove('hidden-section');
                    const signupSubmitBtn = document.getElementById('signup-submit-btn-auth'); if (signupSubmitBtn) signupSubmitBtn.classList.add('hidden-section');
                    const switchToSignupLink = document.getElementById('switch-to-signup'); if (switchToSignupLink) switchToSignupLink.classList.remove('hidden-section');
                    const switchToSigninLink = document.getElementById('switch-to-signin'); if (switchToSigninLink) switchToSigninLink.classList.add('hidden-section');
                    if (window.UIController && typeof window.UIController.showOverlay === 'function') { window.UIController.showOverlay('auth-overlay'); }
                    else { authOverlay.style.display = 'flex'; void authOverlay.offsetHeight; authOverlay.classList.add('show'); }
                    setTimeout(() => { const emailInput = document.getElementById('auth-email'); if (emailInput) emailInput.focus(); }, 100);
                } else { console.error("Auth overlay element not found!"); this.showMessage("Error: Login form not found", 2000, true); }
            },
            MenuUIUtils.COLORS.secondary, 180, 60, 'sizeLg'
        );
        if(this.uiElements.signInButton) this.uiElements.signInButton.setAlpha(0);

        // Smooth, coordinated fade-in animations - minimal and professional
        this.tweens.add({ targets: this.uiElements.title, alpha: 1, duration: 600, ease: 'Sine.easeOut' });
        this.time.delayedCall(200, () => {
            if(this.uiElements.playButton) this.tweens.add({ targets: this.uiElements.playButton, alpha: 1, duration: 400, ease: 'Sine.easeOut' });
            if(this.uiElements.signInButton) this.tweens.add({ targets: this.uiElements.signInButton, alpha: 1, duration: 400, ease: 'Sine.easeOut', delay: 100 });
        });

        this.createFooter(centerX, height - 25);

        this.uiElements.versionText = this.add.text(
            width - this.padding, this.padding,
            `v${window.PROJECT_VERSION || '3.1.0'}`,
            { fontFamily: 'monospace', fontSize: '14px', color: '#AAAAAA' } // Consistent with BootScene
        ).setOrigin(1, 0);

        this.handleResize();
        
        // Override BaseMenuScene - wait for smooth animations to start
        this.time.delayedCall(250, () => {
            this.isUIReady = true;
            console.debug(`[${this.scene.key}] MainMenuScene smooth animations started, ready for fade-in`);
        });
    }

    createFooter(centerX, footerY) {
        const footerStyle = { fontFamily: 'monospace', fontSize: '12px', color: '#888888' };
        const spacing = 100;
        this.uiElements.emailText = this.add.text(centerX - spacing, footerY, 'info@deckops.ai', footerStyle).setOrigin(0.5);
        this.uiElements.termsText = this.add.text(centerX, footerY, 'Terms of use', footerStyle)
            .setOrigin(0.5).setInteractive({ useHandCursor: true })
            .on('pointerdown', () => window.open('/terms.html', '_blank'));
        this.uiElements.privacyText = this.add.text(centerX + spacing, footerY, 'Privacy policy', footerStyle)
            .setOrigin(0.5).setInteractive({ useHandCursor: true })
            .on('pointerdown', () => window.open('/privacy-policy.html', '_blank'));
    }

    handleResize(gameSize) {
        super.handleResize(gameSize);
        if (!this.cameras?.main) return;
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;
        const centerY = height / 2;
        const buttonSpacing = 200;

        if (this.uiElements.title) this.uiElements.title.setPosition(centerX, centerY - 80); // Exact BootScene position
        if (this.uiElements.playButton) this.uiElements.playButton.setPosition(centerX - buttonSpacing / 2, centerY + 50);
        if (this.uiElements.signInButton) this.uiElements.signInButton.setPosition(centerX + buttonSpacing / 2, centerY + 50);
        if (this.uiElements.versionText) this.uiElements.versionText.setPosition(width - this.padding, this.padding);

        const footerY = height - 25;
        const footerSpacing = 100;
        if (this.uiElements.emailText) this.uiElements.emailText.setPosition(centerX - footerSpacing, footerY);
        if (this.uiElements.termsText) this.uiElements.termsText.setPosition(centerX, footerY);
        if (this.uiElements.privacyText) this.uiElements.privacyText.setPosition(centerX + footerSpacing, footerY);
    }

    shutdown() {
        super.shutdown();
    }
}

export default MainMenuScene;